import { PlayerSwap } from "@/api/privateRequests/sbcs/player-swap";
import { useEscape } from "@/hooks/useKeyboardShortcut";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { cn } from "@/lib/utils";
import { BREAKPOINTS } from "@/settings";
import { Player } from "@/types/Player";
import { useEffect, useState } from "react";
import { useMediaQuery } from "usehooks-ts";

import { PlayerCardView } from "./PlayerCardView";
import { PlayerInfoView } from "./PlayerInfoView";
import { SwapButton } from "./SwapButton";
import SwapView from "./SwapView";

type PlayerCardHolderProps = {
  player: Player;
  index: number;
  handlePlayerClicked: (index: number) => void;
  handleSwapClicked: (index: number) => void;
  onSwap: (player: PlayerSwap, index: number) => void;
  showPlayerInfo: boolean;
  showSwapView: boolean;
  playerSwaps?: PlayerSwap[];
  loadingSwaps?: boolean;
  selected?: boolean;
  pitchHeight?: number;
};

const PlayerCardHolder = ({
  player,
  index,
  handlePlayerClicked,
  handleSwapClicked,
  onSwap,
  showSwapView,
  showPlayerInfo,
  playerSwaps,
  loadingSwaps,
  selected,
  pitchHeight,
}: PlayerCardHolderProps) => {
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const isTouch = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const smallDesktop = useMediaQuery(BREAKPOINTS.SMALL_DESKTOP.query);
  const [isHovering, setIsHovering] = useState(false);

  const handleClearSelected = () => {
    if (selected) {
      handlePlayerClicked(undefined);
      setCopied(-1);
    }
  };
  useEscape(() => {
    handleClearSelected();
    handleClosePlayerSwap();
  });

  const handleClosePlayerSwap = () => handleSwapClicked(undefined);

  const ref = useOutsideClick(handleClearSelected);
  const [cardPosition, setCardPosition] = useState<
    | "left-bottom"
    | "left-top"
    | "right-bottom"
    | "right-top"
    | "center"
    | "center-top"
    | "center-bottom"
    | "center-right-bottom"
    | "center-left-bottom"
  >("center");
  const playerSwapRef = useOutsideClick(handleClosePlayerSwap);
  const [copied, setCopied] = useState(-1);

  useEffect(() => {
    const centerX = isMobile ? window.innerWidth / 2 : pitchHeight / 2;
    const centerY = pitchHeight / 2;

    const { offsetLeft, offsetTop, offsetWidth, offsetHeight } = ref.current;

    if (offsetLeft + offsetWidth < centerX) {
      if (offsetTop + offsetHeight < centerY) {
        setCardPosition("left-top");
      } else {
        setCardPosition("left-bottom");
      }
    } else if (offsetLeft > centerX + offsetWidth) {
      if (offsetTop + offsetHeight < centerY) {
        setCardPosition("right-top");
      } else {
        setCardPosition("right-bottom");
      }
    } else {
      if (offsetTop + offsetHeight < centerY) {
        setCardPosition("center-top");
      } else if (offsetTop > centerY) {
        const cardCenterX = offsetLeft + offsetWidth / 2;
        if (cardCenterX >= centerX) {
          setCardPosition("center-right-bottom");
        } else {
          setCardPosition("center-left-bottom");
        }
      } else {
        setCardPosition("center");
      }
    }
  }, [ref, pitchHeight, isMobile]);

  useEffect(() => {
    if (copied !== -1) {
      const timer = setTimeout(() => {
        setCopied(-1);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [copied]);

  const playerInfoIsRight =
    cardPosition === "right-bottom" || cardPosition === "right-top";
  return (
    <>
      <div
        className={cn(
          `playerview absolute text-white ut-squad-slot-view-${index} z-[999] flex flex-col items-center text-xtiny`,
          !selected && "hidden",
          playerInfoIsRight
            ? "-translate-x-full desktop:-translate-x-[135%]"
            : "translate-x-0 desktop:translate-x-[35%]",
        )}
        style={{ ["--slot-offset-y" as any]: "-106%" }}
      >
        <div
          className={cn(
            "p-2 rounded-lg flex flex-col w-fit whitespace-nowrap bg-gray-900 shadow text-tiny mb-1 z-20 transition ease-in-out",
            copied === index ? "opacity-100" : "opacity-0",
          )}
        >
          Copied {player.full_name} to clipboard ✅
        </div>
        <PlayerInfoView player={player} />
      </div>

      <div
        className={cn(
          `absolute ut-squad-slot-view-${index} z-20`,
          cardPosition === "right-bottom" || cardPosition === "right-top"
            ? "translate-x-[-120%] desktop:translate-x-[-100%] sb-large:translate-x-[-115%]"
            : cardPosition === "center" && isMobile
              ? "left-1/2 -translate-x-1/2 mt-26"
              : cardPosition === "center-top" && isMobile
                ? "left-1/2 -translate-x-1/2 mt-26"
                : cardPosition === "center-bottom" && isMobile
                  ? "left-1/2 -translate-x-1/2 -mt-32"
                  : cardPosition === "center-right-bottom" && isMobile
                    ? "left-1/2 -translate-x-1/2 -mt-32"
                    : cardPosition === "center-left-bottom" && isMobile
                      ? "left-1/2 -translate-x-1/2 -mt-32"
                      : "translate-x-[15%] desktop:translate-x-[0%] sb-large:translate-x-[15%]",
        )}
      >
        {showSwapView && playerSwaps && (
          <SwapView
            players={playerSwaps}
            onClick={onSwap}
            index={index}
            ref={playerSwapRef}
          />
        )}
      </div>
      <div
        className={cn(
          `absolute ut-squad-slot-view-${index} -translate-x-1/2 cursor-pointer z-10`,
          isMobile
            ? "scale-100"
            : isTouch
              ? "scale-65"
              : smallDesktop
                ? "scale-85"
                : "scale-90",
        )}
        ref={ref}
      >
        <PlayerCardView
          player={player}
          hide_pos_base={"not_hidden"}
          selected={showPlayerInfo || showSwapView}
          onClick={(e) => {
            navigator.clipboard.writeText(player.full_name);
            handlePlayerClicked(index);
            setCopied(index !== copied ? index : -1);
            e.stopPropagation();
          }}
        />
        <div
          className="absolute bottom-4 w-10 h-10 flex items-center -right-3 bottom-[9px] desktop:-bottom-2 desktop:right-2"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          onClick={(e) => {
            if (Object.values(playerSwaps || {})?.length > 0 && !loadingSwaps) {
              handleSwapClicked(index);
              e.stopPropagation();
            }
          }}
        >
          <div className="relative w-4 h-4 mx-auto desktop:w-6 desktop:h-6">
            {player.card_png_name !== "locked.png" && (
              <SwapButton
                isHovering={isHovering}
                enabled={
                  Object.values(playerSwaps || {})?.length > 0 && !loadingSwaps
                }
                selected={showSwapView}
                loadingSwaps={loadingSwaps}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export { PlayerCardHolder };
